import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

/** What uses this? (references_list): everything that refers to a tag, email, product or access key. */
export type RefKind = "tag" | "email" | "product" | "access";

export interface Reference {
  area: string;
  id?: number;
  key?: string;
  name: string;
  status?: string;
  version?: string;
  where: string[];
  link?: string;
}

export interface RefsOut {
  kind: RefKind;
  id?: number;
  key?: string;
  name: string;
  archived: boolean;
  summary: string;
  references: Reference[];
}

const AREA_LABEL: Record<string, string> = {
  automation: "Automations",
  form: "Website forms",
  segment: "Segments",
  event: "Events",
  site: "Sites",
  access_rule: "Access rules",
  product: "Products",
  broadcast: "Broadcasts",
};

function refsPath(kind: RefKind, idOrKey: number | string) {
  const q = new URLSearchParams({ kind });
  if (kind === "access") q.set("key", String(idOrKey));
  else q.set("id", String(idOrKey));
  return `/api/v1/references?${q.toString()}`;
}

/** Loads the references (also used for the summary line in archive confirmations). */
export function fetchReferences(kind: RefKind, idOrKey: number | string) {
  return api<RefsOut>(refsPath(kind, idOrKey));
}

/** "Used by 2 automations and 1 form." to put in front of an archive or change confirmation (empty on error). */
export async function referenceSummary(kind: RefKind, idOrKey: number | string): Promise<string> {
  try {
    return (await fetchReferences(kind, idOrKey)).summary;
  } catch {
    return "";
  }
}

/** The What uses this? panel: grouped by area, each row linking to its page, automation versions labeled. */
export function WhatUsesThis({ kind, idOrKey, onClose }: { kind: RefKind; idOrKey: number | string; onClose: () => void }) {
  const q = useQuery({ queryKey: ["references", kind, idOrKey], queryFn: () => fetchReferences(kind, idOrKey) });
  const groups = new Map<string, Reference[]>();
  for (const r of q.data?.references ?? []) groups.set(r.area, [...(groups.get(r.area) ?? []), r]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogTitle>What uses {q.data?.name ? `“${q.data.name}”` : `this ${kind}`}?</DialogTitle>
        <DialogDescription>
          {q.data ? q.data.summary : "Looking at automations, forms, segments, events, sites, access rules, products and broadcasts…"} Changing or archiving it affects what is listed here.
        </DialogDescription>
        {q.isPending && <Skeleton className="h-24" />}
        {q.isError && <p className="text-sm text-[var(--color-destructive)]">{(q.error as Error).message}</p>}
        {q.data && q.data.references.length === 0 && <p className="text-sm text-[var(--color-muted-foreground)]">No references found.</p>}
        <div className="max-h-[60vh] space-y-4 overflow-y-auto">
          {[...groups.entries()].map(([area, rows]) => (
            <section key={area} aria-label={AREA_LABEL[area] ?? area}>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">{AREA_LABEL[area] ?? area}</h3>
              <ul className="space-y-2">
                {rows.map((r, i) => (
                  <li key={`${r.area}-${r.id ?? r.key}-${r.version ?? ""}-${i}`} className="rounded border border-[var(--color-border)] p-2 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      {r.link ? (
                        <a href={r.link} className="font-medium text-[var(--color-primary)] hover:underline">
                          {r.name}
                        </a>
                      ) : (
                        <span className="font-medium">{r.name}</span>
                      )}
                      {r.status && <Badge variant="muted">{r.status}</Badge>}
                      {r.version && <span className="text-xs text-[var(--color-muted-foreground)]">{r.version}</span>}
                    </div>
                    <ul className="mt-1 list-disc pl-5 text-xs text-[var(--color-muted-foreground)]">
                      {r.where.map((w) => (
                        <li key={w}>{w}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
